import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { AUTH_REFRESH } from '@/core/auth/tokens/auth-session.token';
import { AuthStore } from '@/core/auth/stores/auth.store';

/**
 * Builds a guard that restores the session (via AUTH_REFRESH) before applying
 * `resolve`. Every route guard shares this restore-then-resolve dance; changes
 * to session restoration belong here, not in each guard.
 *
 * Login is the right landing spot when a route needs a session it could not restore. Routes that
 * only want to *know* whether there is one — and stay put either way — override `onRestoreFailure`.
 */
export function sessionGuard(
  resolve: (store: AuthStore, router: Router) => boolean | UrlTree,
  onRestoreFailure: (router: Router) => boolean | UrlTree = (router) => router.parseUrl('/login'),
): CanActivateFn {
  return () => {
    const store = inject(AuthStore);
    const router = inject(Router);

    if (store.isAuthenticated()) return resolve(store, router);

    const refresh = inject(AUTH_REFRESH, { optional: true });
    if (!refresh) return onRestoreFailure(router);

    return refresh().pipe(
      map(() => resolve(store, router)),
      catchError(() => of(onRestoreFailure(router))),
    );
  };
}
